// Thin fetch wrapper. All API routes live under /api and use httpOnly session cookie.

const BASE = import.meta.env.DEV ? 'http://localhost:8788' : ''

export type ApiError = { error: string; code?: string }

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  })
  const text = await res.text()
  const data = text ? JSON.parse(text) : null
  if (!res.ok) {
    const err: ApiError = data ?? { error: res.statusText }
    throw Object.assign(new Error(err.error), err)
  }
  return data as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

// Typed endpoints
export type SignupInput = { email: string; password: string; fullName: string }
export type LoginInput = { email: string; password: string }
export type LeadInput = {
  productSlug: string
  plan: 'starter' | 'pro' | 'business'
  businessName: string
  ownerName: string
  phone: string
  email: string
  website?: string
  city: string
  country: string
  notes?: string
  customIdea?: string
  budget?: number
  /** Optional +€149 add-on (Business tier only): custom domain + branded setup. */
  customDomain?: boolean
}

export type Lead = LeadInput & {
  id: string
  referrerId: string
  referrerCode: string
  status: 'new' | 'contacted' | 'won' | 'lost' | 'paid'
  commissionSetup: number
  commissionMonthly: number
  commissionDomain: number // €0 if customDomain=false, €44.70 if true
  createdAt: string
}

export type ReferrerStats = {
  leadsTotal: number
  leadsNew: number
  leadsWon: number
  leadsPaid: number
  commissionPending: number
  commissionPaid: number
  conversionRate: number
  rank: number
  clicks: number
  clickToLeadRate: number
}

export type Commission = {
  id: string
  kind: 'setup' | 'monthly'
  amount: number
  monthIndex: number | null
  status: 'pending' | 'paid'
  paidAt: string | null
  createdAt: string
  referrerEmail: string
  referrerName: string
  businessName: string
  productSlug: string
}

export type AdminReferrer = {
  id: string
  email: string
  fullName: string
  referralCode: string
  status: string
  createdAt: string
  leadsTotal: number
  leadsWon: number
  commissionPaid: number
}
